import { useMemo, type ReactElement } from 'react';
import { Grid } from './Grid';
import { BonusLinkerProps } from './types';
import { FilterForm, useFilterState, initialFilter, type FilterState } from '../filter';
import { HeaderRow } from './HeaderRow';
import { FilterRow } from './FilterRow';
import { Submit, useForm } from '@ff-it/form';
import { useLinkerState } from './useLinkerState';
import { LinkerSummary } from '../LinkerSummary';
import { Box } from '@ff-it/ui';
import { Export } from './Export';
import { calcSummaryState } from './calcSummaryState';
import { Portal } from 'components/Portal';
import { useColumnConfig } from 'components/ControlledTable/useColumnConfig';
import { ColumnConfigPopover } from 'components/ControlledTable/ColumnConfigPopover';

// The hideable columns. `filters` lists the filter fields rendered inside that column's cell (see useFilterFields),
// so hiding the column clears them - otherwise a filter would keep applying while its input is unmounted/unreachable.
const configurableColumns: { key: string; label: string; filters?: (keyof FilterState)[] }[] = [
  { key: 'client', label: 'Client', filters: ['client', 'payer'] },
  { key: 'supplier', label: 'Outlet / Provider', filters: ['outlet_or_service_category', 'provider', 'activity'] },
  { key: 'block', label: 'Block', filters: ['block', 'type'] },
  { key: 'invoices', label: 'Invoices', filters: ['invoice'] },
  { key: 'source', label: 'Target' },
  { key: 'rate', label: 'Rate' },
  { key: 'invoiceable', label: 'Invoiceable' },
  { key: 'invoiced', label: 'Invoiced' },
];
const columnConfigParam = configurableColumns.map(({ key, label }) => ({ key, configLabel: label }));

export function BonusLinker({ disabled = false, invoice, direction, url, ...props }: BonusLinkerProps): ReactElement {
  const form = useForm();
  const [filterState, setFilterState] = useFilterState();
  const { configuredColumns, configurable, columnActions } = useColumnConfig('bonus-linker::cols', columnConfigParam);
  const visibleKeys = useMemo(
    () => new Set(configuredColumns.filter((c) => c.visible).map((c) => String(c.column.key))),
    [configuredColumns],
  );
  const effectiveFilter = useMemo<FilterState>(() => {
    const overrides = configurableColumns
      .filter((col) => !visibleKeys.has(col.key))
      .flatMap((col) => (col.filters ?? []).map((f) => [f, initialFilter[f]] as const));
    return overrides.length ? { ...filterState, ...Object.fromEntries(overrides) } : filterState;
  }, [filterState, visibleKeys]);
  const { rows, bound } = useLinkerState(props, effectiveFilter, disabled);
  const summary = calcSummaryState(rows, bound, invoice);
  const unlink =
    !disabled && summary.hasBound
      ? () => {
          form.change('bound', {});
        }
      : undefined;

  const link =
    !disabled && summary.hasAvailable
      ? () => {
          form.change('bound', {
            ...bound,
            ...rows.reduce((acc: Record<string, string>, { id, bound, balance }) => {
              const key = `_${id}`;
              if (bound === null && !balance.eq(0)) {
                acc[key] = balance.toFixed();
              }
              return acc;
            }, {}),
          });
        }
      : undefined;
  return (
    <>
      <Portal>
        <Export url={url} />
        {!disabled && <Submit className="ml-3" size="sm" />}
        <Box marginLeft="auto" display="flex" alignItems="center" gap="2" className="pr-1">
          {invoice && <LinkerSummary direction={direction} state={summary} mb="1" />}
          {configurable && (
            <ColumnConfigPopover configuredColumns={configuredColumns} {...columnActions} showPins={false} />
          )}
        </Box>
      </Portal>
      <HeaderRow visibleKeys={visibleKeys} />
      <FilterForm filter={filterState} setFilter={setFilterState} disabled={false}>
        <FilterRow entities={props.entities} summary={summary} unlink={unlink} link={link} visibleKeys={visibleKeys} />
      </FilterForm>
      <Grid rows={rows} entities={props.entities} disabled={disabled} visibleKeys={visibleKeys} />
    </>
  );
}
